import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import {
  CommonReturnDTO,
  PostalCodeDTO,
  CommonDataDTO,
  CallTariffDTO,
  TariffDTO,
  EstablishmentDTO,
  EstablishmentByUserDTO,
  FinancialProduct,
  SectorDTO,
  ModalityPaymentDTO,
  FormPaymentDTO,
  ComboDTO,
  ComercialProductResponseDTO,
  VendorComboDTO,
} from '../model/combos-model';
import { AgentDTO, PrescriberContactData } from '../model/requests-model';
import { CommercialDTO } from '@app/payment-for-certifications/model/payment-model';

@Injectable()
export class CombosApi {
  constructor(private _http: HttpClient) {}

  getCommonCombos(common: any): Observable<CommonReturnDTO> {
    return this._http.post<CommonReturnDTO>(environment.URL_BASE_REQUEST + '/api/product/combosCommon', common);
  }

  getTownsList(postalCode: PostalCodeDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + '/api/product/postalCode', postalCode);
  }

  getTariffs(callTariffDTO: CallTariffDTO): Observable<TariffDTO[]> {
    return this._http.post<TariffDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlTarifas, callTariffDTO);
  }

  getCommercialProduct(establishment: EstablishmentDTO): Observable<ComercialProductResponseDTO[]> {
    return this._http.post<ComercialProductResponseDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlCommercialProduct,
      establishment
    );
  }

  getCommercialProductForCombos(establishment: EstablishmentDTO): Observable<ComercialProductResponseDTO[]> {
    return this._http.post<ComercialProductResponseDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlCommercialProductForCombos,
      establishment
    );
  }

  getLineProduct(establishmentByUser: EstablishmentByUserDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlProductLine,
      establishmentByUser
    );
  }

  getExpirationDays(financialProduct: FinancialProduct): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlExpirationDays,
      financialProduct
    );
  }

  getProduct(sector: SectorDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlProduct, sector);
  }

  getModalityPayment(modalityPayment: ModalityPaymentDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlModalityPayment,
      modalityPayment
    );
  }

  getFormPayment(formPayment: FormPaymentDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlFormPayment, formPayment);
  }

  getStatusRequests(): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlStateRequests, null);
  }

  getEstablishment(filter: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlEstablishments, filter);
  }
  getDivisions(): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlDivision, null);
  }

  getOffices(filter: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlOffices, filter);
  }

  getChains(filter: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subURlChains, filter);
  }

  getPrescribers(filter: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlPrescribers, filter);
  }

  getEstablishments(filter: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlEstablishments, filter);
  }

  getEstablishmentsByPrescribers(request: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlgetEstablishmentsByPrescribers,
      request
    );
  }

  getPrescribersByChains(request: any): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlgetPrescribersByChains,
      request
    );
  }

  getVendor(establishment: string): Observable<VendorComboDTO[]> {
    return this._http.post<VendorComboDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlVendors, null, {
      params: { establishmentId: establishment },
    });
  }

  getUserById(userId: string): Observable<CommonDataDTO> {
    return this._http.post<CommonDataDTO>(environment.URL_BASE_REQUEST + environment.subUrlUserById, null, {
      params: { userId: userId },
    });
  }

  getEmploymentSituation(comboDTO: ComboDTO): Observable<any> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlEmploymentSituation,
      comboDTO
    );
  }

  getSectorBusiness(comboDTO: ComboDTO): Observable<any> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlSectorBusiness, comboDTO);
  }

  public getCnae(comboDTO: ComboDTO): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlCnae, comboDTO);
  }

  public getPrescriberData(): Observable<PrescriberContactData> {
    return this._http.post<PrescriberContactData>(
      environment.URL_BASE_REQUEST + environment.subUrlPrescriberData,
      null
    );
  }

  getChainsFromMacroUser(user: string): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlChainsByMacroUser, null, {
      params: new HttpParams().set('macroUser', user),
    });
  }

  getOfficesByMacroUser(user: string): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlOfficesByMacroUser, null, {
      params: new HttpParams().set('macroUser', user),
    });
  }

  getAllChainsByDivision(division: string): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlChainByDivision, null, {
      params: new HttpParams().set('divisionId', division),
    });
  }

  getAllOfficesByDivision(division: string): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(environment.URL_BASE_REQUEST + environment.subUrlOfficesByDivision, null, {
      params: new HttpParams().set('divisionId', division),
    });
  }

  getEstablishmentsWithCertifiedPaymentsByExternalCommercial(
    commercialDTO: CommercialDTO
  ): Observable<CommonDataDTO[]> {
    return this._http.post<CommonDataDTO[]>(
      environment.URL_BASE_REQUEST + environment.subUrlGetUsersWithPaymentCertified,
      commercialDTO
    );
  }
}
